// RCPT1-S7 (docs/build-briefs/RCPT1-S7-printers-settings-screen.md). The pure parts of Settings > Location > Receipt printers:
// what a printer row says, which actions it offers, the server URL a manager types into the printer's web page, and the setup
// steps. No DOM and no network, so a node:test file can import it.

import type { PrinterDto } from "./api.ts";

export const SECTION_TITLE = "Receipt printers";
export const ADD_LABEL = "+ Add printer";
export const NONE_LINE = "No printers yet. Add one to print receipts.";
/** Both the pairing code and a changed password live this long (src/config/printers.ts). */
export const CODE_LIFE_LINE = "Expires in 60 minutes";
export const ONCE_LINE = "shown once";

/** The steps a manager follows on the printer's own web page. */
export const SETUP_STEPS: readonly string[] = [
  "Open the printer's web page",
  "Open CloudPRNT settings",
  "Enter the server URL",
  "Enter the code as the password",
];

export type PrinterBadge = {
  label: "Online" | "Offline" | "Not paired";
  tone: "good" | "bad" | "neutral";
};

/** A removed printer is not listed. */
export function visiblePrinters(printers: readonly PrinterDto[]): PrinterDto[] {
  return printers.filter((printer) => printer.status !== "unpaired");
}

export function printerBadge(printer: PrinterDto): PrinterBadge {
  if (printer.status !== "paired") return { label: "Not paired", tone: "neutral" };
  return printer.online ? { label: "Online", tone: "good" } : { label: "Offline", tone: "bad" };
}

export function printerFlags(printer: PrinterDto): string[] {
  const flags: string[] = [];
  if (printer.passwordExpired) flags.push("Password expired");
  if (printer.passwordChangePending) flags.push("Password change pending");
  return flags;
}

export interface PrinterActions {
  testPage: boolean;
  changePassword: boolean;
  unpair: boolean;
}

/** Test page and a password change need a paired printer. Unpair is for any printer still listed. */
export function printerActions(printer: PrinterDto): PrinterActions {
  const paired = printer.status === "paired";
  return { testPage: paired, changePassword: paired, unpair: printer.status !== "unpaired" };
}

export function jobsLine(printer: PrinterDto): string {
  if (printer.openJobs <= 0) return "";
  return printer.openJobs === 1 ? "1 job waiting" : `${printer.openJobs} jobs waiting`;
}

/** The exact address the printer polls: the API's own origin plus the path the server named. */
export function cloudPrntUrl(
  apiBaseUrl: string,
  cloudPrntPath: string,
  pageOrigin: string,
): string {
  const base = apiBaseUrl.trim() === "" ? pageOrigin : apiBaseUrl;
  return new URL(cloudPrntPath, base).toString();
}

export function validName(raw: string): { ok: true; name: string } | { ok: false; error: string } {
  const name = raw.trim();
  if (name.length === 0) return { ok: false, error: "Enter a name" };
  if (name.length > 60) return { ok: false, error: "Up to 60 characters" };
  return { ok: true, name };
}

export function unpairTitle(name: string): string {
  return `Unpair ${name}?`;
}
